/* PostHog pulse: one row per product with its key events over the last 24h against the prior 7-day daily average,
   a 14-day sparkline each, and the product's main funnel. A stopped heartbeat (visits/opens) or a big drop on a busy
   event goes red. `o` opens the product page (/product/<company>), `v` opens PostHog. API: productPulse.get. */
import type { inferRouterOutputs } from "@trpc/server"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { Sparkline } from "../../ProductPulseScreen/PulseCharts"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { deltaPct, formatDelta, funnelSummary, pulseVerdict, referenceDaily } from "./productPulseModel"
import { WIDGET_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TPulse = inferRouterOutputs<AppRouter>["productPulse"]["get"]
type TProduct = TPulse["products"][number]
type TMetric = TProduct["metrics"][number]

const SPARK_DAYS = 14

const delta = (m: TMetric) => formatDelta(deltaPct(m.current, referenceDaily(m)))

function MetricLines({ product }: { product: TProduct }) {
  return (
    <ul className="space-y-1">
      {product.metrics.map((m) => (
        <li key={m.event} className="flex items-center gap-2 tabular-nums">
          <span className="w-28 shrink-0 truncate">{m.label}</span>
          <Sparkline values={m.daily.slice(-SPARK_DAYS)} />
          <span className="min-w-0 truncate text-muted-foreground">
            {m.current.toLocaleString()} 24h · {m.baselineDaily.toLocaleString()}/day avg
            {m.expectedDaily ? ` · expected ≥${m.expectedDaily}` : ""} · {delta(m)}
          </span>
        </li>
      ))}
    </ul>
  )
}

const productRow = (product: TProduct): IWidgetRow => {
  const { verdict, reason } = pulseVerdict(product)
  const summary = product.metrics.map((m) => `${m.label} ${m.current.toLocaleString()} (${delta(m)})`).join(" · ")
  const funnel = product.funnel ? funnelSummary(product.funnel.steps) : null
  return {
    key: product.company,
    verdict,
    title: product.label,
    sub: product.error ? `PostHog error: ${product.error}` : summary,
    meta: verdict === EVerdict.CLEAR ? undefined : reason,
    company: product.company,
    href: `/product/${product.company}`,
    hrefLabel: "Open pulse page",
    actions: [{ key: "v", label: "Open in PostHog", run: () => window.open(product.url, "_blank", "noopener") }],
    details: (
      <>
        <p>{reason}</p>
        <MetricLines product={product} />
        {product.funnel && funnel && (
          <p className="mt-2">
            <span className="text-muted-foreground">{product.funnel.name}: </span>
            {funnel}
          </p>
        )}
        {product.warning && <p className="mt-1 text-muted-foreground">{product.warning}</p>}
      </>
    ),
  }
}

const renderPulse = (data: TPulse): IWidgetResult => {
  const now = Date.now()
  const rows = data.products.map(productRow)
  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: `${rows.length} products · 24h vs 7d avg`,
    rows,
    empty: "No products configured.",
    foot: `PostHog · refreshed ${ago(data.generatedAt, now)} · cached 5 min`,
  }
}

export const productPulseWidget: IWidgetDefinition = {
  id: "product-pulse",
  title: "Product pulse",
  description: "PostHog key events per product, last 24h vs 7-day average, with 14-day trends and main funnel",
  columns: { 2: 2, 3: 3, 4: 4, 5: 4, 6: 5 },
  useWidget: () => useQueryWidget(trpc.productPulse.get.useQuery(undefined, WIDGET_QUERY_OPTIONS), renderPulse),
}
